import type { BlockProductPlans } from 'types/block-product-plans';
import type { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import camelcaseKeys from 'camelcase-keys';
import { usePostHog } from 'posthog-js/react';

import { Button, Paragraph, Wrap } from '@leafwell/components';

import { getRandomInt } from 'lib/utils/generateRandomInt';
import { useMatchMedia } from 'hooks/useMatchMedia';
import { request } from 'lib/rest';
import { toQueryString } from 'lib/utils/toQueryString';

import HeadingBody from './heading-body';
import Figure from './figure';
import { useEventsTracker } from 'contexts/eventsTracker';

type PartnerProps = {
  membership?: {
    partnerMessage?: string;
    discountCode?: string;
    discountValue?: number;
  };
};

const DiscountMedalion: React.FC<{ discountValue?: number }> = ({
  discountValue,
}) => {
  return (
    <div className="relative w-16">
      <Image
        src="/discount-medallion-orange.svg"
        alt=""
        width={89}
        height={93}
        className="w-full h-full"
        priority
      />
      <span className="absolute inset-0 flex flex-col items-center justify-center text-white font-semibold text-base leading-none">
        <span>{discountValue}%</span>
        <span>off</span>
      </span>
    </div>
  );
};

const ChevronIconSvg: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <span className={className}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        fill="none"
      >
        <path
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
          d="m8.558 12.442.884-.884L15 17.116l5.558-5.558.884.884L15 18.884l-6.442-6.442Z"
        />
      </svg>
    </span>
  );
};

const ProductPlanCard: React.FC<
  BlockProductPlans['plans'][0] & {
    instanceId?: string | null;
    partnerData?: PartnerProps;
    asPath?: string;
    query?: ParsedUrlQuery;
  }
> = ({
  instanceId,
  planName,
  subscribeUrl,
  isPremium,
  pricePerMonth,
  pricePerYear,
  freeMonthsIfApplyAnual,
  whatsIncluded,
  planTotalSavings,
  savingsDetails,
  partnerData,
  asPath,
  query,
}) => {
  const { trackEvent } = useEventsTracker();

  const { discountCode, discountValue } = partnerData?.membership;

  const pricePerMonthDiscount = discountValue
    ? pricePerMonth - pricePerMonth * (discountValue / 100)
    : 0;
  const pricePerYearDiscount = discountValue
    ? pricePerYear - pricePerYear * (discountValue / 100)
    : 0;

  const [isSmallScreen] = useMatchMedia(['(max-width: 1024px)'], [true]);
  const [isOpen, setIsOpen] = useState(false);

  const posthog = usePostHog();
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();

  const product: 'membership' | 'telepharmacist' | null = asPath.includes(
    '/membership',
  )
    ? 'membership'
    : asPath.includes('/cannabis-care')
    ? 'telepharmacist'
    : null;
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const queryParams = toQueryString(query);

  useEffect(() => {
    if (product) {
      setNewUrl(
        subscribeUrl?.includes('?')
          ? `${subscribeUrl}&session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`
          : `${subscribeUrl}?session_id=${sessionId}&distinct_id=${distinctId}${
              queryParams ? `&${queryParams}` : ''
            }`,
      );
    }
  }, [sessionId, distinctId, product, queryParams]);

  return (
    <div className="grid gap-6 justify-items-center">
      <li className="relative grid gap-8 justify-items-center rounded-3xl border-2 p-8 bg-white border-gray-300 md:w-128">
        {discountValue ? (
          <div className="absolute top-2 left-2">
            <DiscountMedalion discountValue={discountValue} />
          </div>
        ) : null}
        {isPremium ? (
          <div className="absolute top-0 right-0">
            <Image src="/diamond-yellow.svg" width={76} height={81} alt="" />
          </div>
        ) : null}
        <div className="grid gap-4">
          {planName && (
            <HeadingBody
              level="6"
              title={planName}
              className="text-center font-semibold"
            />
          )}
          <div className="flex flex-col">
            {discountValue ? (
              <Paragraph
                className={[
                  '!text-xl text-center font-semibold mb-2',
                  isPremium ? 'text-purple-500' : 'text-green-600',
                ].join(' ')}
              >
                Regularly ${pricePerMonth}/month
              </Paragraph>
            ) : null}
            <Paragraph
              className={[
                '!text-6xl font-semibold text-center',
                isPremium ? 'text-purple-500' : 'text-green-600',
              ].join(' ')}
            >
              {`$${
                pricePerMonthDiscount > 0
                  ? pricePerMonthDiscount.toFixed(2)
                  : pricePerMonth
              }`}
            </Paragraph>
            <Paragraph
              className={[
                '!text-xl font-semibold text-center',
                isPremium ? 'text-purple-500' : 'text-green-600',
              ].join(' ')}
            >
              /month
              <br />
              <span className="text-sm">(12-month plan)</span>
            </Paragraph>
            {discountCode ? (
              <Paragraph
                className={[
                  '!text-xl text-center',
                  isPremium ? 'text-purple-500' : 'text-green-600',
                ].join(' ')}
              >
                Use code <span className="font-semibold">{discountCode}</span>
              </Paragraph>
            ) : null}
          </div>
          <Paragraph className="text-center text-[#677A89] font-semibold !text-xl">
            {`or $${
              pricePerYearDiscount > 0
                ? pricePerYearDiscount.toFixed(2)
                : pricePerYear
            }/year`}
            <br />
            <span className="text-sm">
              {`(get ${freeMonthsIfApplyAnual} months free!)`}
            </span>
          </Paragraph>
        </div>
        {whatsIncluded?.length > 0 ? (
          <ul className="grid">
            {whatsIncluded.map(({ item }, index) => {
              const idx = getRandomInt(1, 1000000);
              return (
                <li
                  key={`${idx}-product-plan-card-whats-included`}
                  className={[
                    'flex items-center gap-x-6 border-b-gray-300 border-b',
                    index === 0 ? 'pb-4 ' : 'py-4',
                  ].join(' ')}
                >
                  <Image src="/check-green.svg" width={18} height={18} alt="" />
                  <Paragraph>{item}</Paragraph>
                </li>
              );
            })}
          </ul>
        ) : null}
        <Button
          label={`Join ${planName}`}
          href={product ? newUrl : subscribeUrl}
          rounded={true}
          variant={isPremium ? 'secondary' : 'primary'}
          className="w-full h-fit"
          onClick={() => {
            trackEvent({
              event: 'select_content',
              content_type: `Join ${planName}`,
              item_id: `ProductPlan-${instanceId}`,
              action: `ProductPlan-join-plan`,
            });
            if (product) {
              trackEvent({
                event: 'generate_lead',
                item_id: `ProductPlan-${instanceId}`,
                service: product,
                product_plan: planName,
                page_referrer: document.referrer,
              });
            }
          }}
        />
      </li>
      <div className="grid gap-y-4">
        <Paragraph className="grid gap-y-3 !text-xl font-semibold">
          <span>{`${planName} membership value`}</span>
          <span className="!text-2xl">{`$${planTotalSavings}+`}</span>
        </Paragraph>
        <a
          className={[
            'flex gap-x-1 items-center justify-center text-center font-semibold hover:underline',
            isSmallScreen && savingsDetails?.length > 0 && !isOpen
              ? 'block'
              : 'hidden',
          ].join(' ')}
          onClick={() => setIsOpen(!isOpen)}
        >
          Show more
          <ChevronIconSvg />
        </a>
        {savingsDetails?.length > 0 ? (
          <ul
            className={[
              'grid gap-y-3',
              isSmallScreen
                ? isOpen
                  ? 'block transition-max-height duration-200 ease-out'
                  : 'hidden transition-max-height duration-200 ease-out'
                : '',
            ].join(' ')}
          >
            {savingsDetails.map(({ savingItem, icon }) => {
              const idx = getRandomInt(1, 1000000);
              return (
                <li
                  key={`${idx}-product-plan-card-saving-details`}
                  className="flex gap-x-5 items-center"
                >
                  {icon && (
                    <Figure
                      {...icon}
                      className="inline-flex items-center justify-center w-10"
                    />
                  )}
                  <Paragraph className="!text-base">{savingItem}</Paragraph>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

const ProductPlans: React.FC<BlockProductPlans> = ({
  sectionId,
  instanceId,
  title,
  description,
  plans,
}) => {
  const { asPath, query } = useRouter();
  const [partnerData, setPartnerData] = useState<PartnerProps>({
    membership: {
      discountCode: '',
      discountValue: 0,
    },
  });

  useEffect(() => {
    if (query.utm_source && asPath?.includes('/membership')) {
      // call api to fetch partner data based on `utm_source`
      request(`/partner?slug=${query.utm_source}`).then(res => {
        if (res.data && res.data?.totalItems > 0) {
          const { acf } = camelcaseKeys(res.data.items[0], {
            deep: true,
          });
          const { membership } = acf;

          setPartnerData({
            membership,
          });
        }
      });
    }
  }, [query]);

  return (
    <section
      id={sectionId}
      className="bg-lilac-50 my-12 md:my-16"
      itemID={`ProductPlans-${instanceId}`}
    >
      <Wrap>
        {title || description ? (
          <div className="grid gap-y-6 justify-items-center">
            {title && (
              <HeadingBody
                level="2"
                tagName="h2"
                title={title}
                className="font-semibold text-center"
              />
            )}
            {description && (
              <HeadingBody
                level="6"
                tagName="p"
                title={description}
                className="text-center lg:max-w-2xl"
              />
            )}
          </div>
        ) : null}
        {plans?.length > 0 ? (
          <ul className="flex flex-wrap justify-center gap-x-24 gap-y-8">
            {plans.map(
              ({
                planName,
                subscribeUrl,
                isPremium,
                pricePerMonth,
                pricePerYear,
                freeMonthsIfApplyAnual,
                whatsIncluded,
                planTotalSavings,
                savingsDetails,
              }) => {
                const idx = getRandomInt(1, 1000000);
                return (
                  <ProductPlanCard
                    key={`${idx}-product-plan-card`}
                    instanceId={instanceId}
                    planName={planName}
                    subscribeUrl={subscribeUrl}
                    isPremium={isPremium}
                    pricePerMonth={pricePerMonth}
                    pricePerYear={pricePerYear}
                    freeMonthsIfApplyAnual={freeMonthsIfApplyAnual}
                    whatsIncluded={whatsIncluded}
                    planTotalSavings={planTotalSavings}
                    savingsDetails={savingsDetails}
                    partnerData={partnerData}
                    asPath={asPath}
                    query={query}
                  />
                );
              },
            )}
          </ul>
        ) : null}
      </Wrap>
    </section>
  );
};

export default ProductPlans;
